/**
 * Socket: typed WebSocket wrapper for the /ws protocol (DESIGN 5.3) with
 * reconnect-by-token and exponential backoff (DESIGN 5.7).
 *
 * Responsibilities: open the WebSocket, send `hello` on every open (with the
 * token learned from `welcome` so a reconnect rebinds to the same player),
 * send `ping` every NET.pingIntervalMs while open (ServerClock turns the
 * pongs into a server-time estimate), dispatch typed handlers, and retry
 * with backoff 0.5 s -> 8 s after an unexpected close. Frames sent while
 * closed are dropped: a stale input is worse than none.
 *
 * The WebSocket constructor and clock are injectable so the class runs in
 * node tests; the only globals touched are read through `globalThis`.
 */

import type { ClientMsg, ServerMsg } from '@shared/index.js';
import { NET, PROTOCOL_VERSION } from '@shared/index.js';

export type ServerMsgType = ServerMsg['t'];
export type ServerMsgOf<T extends ServerMsgType> = Extract<ServerMsg, { t: T }>;
export type ServerMsgHandler<T extends ServerMsgType> = (m: ServerMsgOf<T>) => void;
export type AnyMsgHandler = (m: ServerMsg) => void;
export type StatusHandler = (connected: boolean, attempt: number) => void;

/** What `hello` carries (DESIGN 5.3). `token` resumes a session after a reconnect or refresh. */
export interface HelloParams { name: string; color?: number; token?: string | null }

/** The subset of the WebSocket API the client uses (structural, so a fake works in tests). */
export interface WebSocketLike {
  readonly readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}
export type WebSocketCtor = new (url: string) => WebSocketLike;

export interface SocketOptions {
  /** Defaults to the browser's WebSocket. */
  WebSocket?: WebSocketCtor;
  /** Local ms clock for ping `c` values; defaults to performance.now(). */
  now?: () => number;
  /** Backoff jitter source in [0, 1); defaults to Math.random. */
  random?: () => number;
}

const WS_OPEN = 1;
const SERVER_TYPES: ReadonlySet<string> = new Set<ServerMsgType>(['welcome', 'room', 'snap', 'pong', 'end', 'err', 'chat']);

/**
 * Server close codes (server/src/config.ts CLOSE_CODE) after which retrying would loop forever:
 * 4000 abuse limits, 4001 out-of-date client, 4003 the session was taken over by another tab.
 */
export const FINAL_CLOSE_CODES: ReadonlySet<number> = new Set([4000, 4001, 4003]);
export type GiveUpHandler = (code: number) => void;

/** Backoff jitter: up to +25% so a restarted server is not hit by every client at once. */
const BACKOFF_JITTER = 0.25;

/** ws(s)://host/ws for the page that loaded the client (DESIGN 5.1). */
export function defaultWsUrl(): string {
  const loc = (globalThis as { location?: { protocol: string; host: string } }).location;
  if (!loc) return 'ws://localhost:8080/ws';
  const scheme = loc.protocol === 'https:' ? 'wss' : 'ws';
  return scheme + '://' + loc.host + '/ws';
}

function resolveWebSocket(): WebSocketCtor {
  const ctor = (globalThis as { WebSocket?: WebSocketCtor }).WebSocket;
  if (!ctor) throw new Error('WebSocket is not available in this environment');
  return ctor;
}

function defaultNow(): number {
  const perf = (globalThis as { performance?: { now(): number } }).performance;
  return perf ? perf.now() : Date.now();
}

export class Socket {
  private _connected = false;
  private ws: WebSocketLike | null = null;
  private url: string | null = null;
  private hello: HelloParams | null = null;
  private _token: string | null = null;
  private closedByUser = false;
  private backoffMs: number = NET.reconnectBackoffMinMs;
  private _attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private readonly handlers = new Map<ServerMsgType, Set<AnyMsgHandler>>();
  private readonly anyHandlers = new Set<AnyMsgHandler>();
  private readonly statusHandlers = new Set<StatusHandler>();
  private readonly giveUpHandlers = new Set<GiveUpHandler>();
  private readonly wsCtor: WebSocketCtor | null;
  private readonly now: () => number;
  private readonly random: () => number;

  constructor(opts: SocketOptions = {}) {
    this.wsCtor = opts.WebSocket ?? null;
    this.now = opts.now ?? defaultNow;
    this.random = opts.random ?? Math.random;
  }

  /** True while the WebSocket is open. */
  get connected(): boolean { return this._connected; }
  /** Reconnect token learned from `welcome` (null before the first welcome). */
  get token(): string | null { return this._token; }
  /** Reconnect attempts since the last successful open (0 while connected). */
  get attempt(): number { return this._attempt; }

  /** Set (or replace) what the next `hello` says. Call before connect(); the token is kept once learned. */
  setHello(hello: HelloParams): void {
    this.hello = hello;
    if (hello.token) this._token = hello.token;
  }

  /**
   * Open (or re-open) the connection. `url` is the ws(s):// endpoint
   * (defaults to the page's /ws); the socket sends `hello` itself on open
   * (with the stored token when reconnecting) and retries with backoff
   * 0.5 s -> 8 s on close.
   */
  connect(url: string = defaultWsUrl(), hello?: HelloParams): void {
    if (hello) this.setHello(hello);
    this.url = url;
    this.closedByUser = false;
    this.clearReconnect();
    this.backoffMs = NET.reconnectBackoffMinMs;
    this._attempt = 0;
    this.dropSocket();
    this.open();
  }

  /** Serialise and send one frame. Frames sent while closed are dropped (inputs are not worth queueing). */
  send(msg: ClientMsg): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WS_OPEN) return;
    ws.send(JSON.stringify(msg));
  }

  /** Subscribe to one message type. Returns an unsubscribe function. */
  on<T extends ServerMsgType>(type: T, fn: (m: ServerMsgOf<T>) => void): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    const h = fn as AnyMsgHandler;
    set.add(h);
    return () => { set.delete(h); };
  }

  /** Subscribe to every message (ClientState.onServerMsg). Returns an unsubscribe function. */
  onAny(fn: AnyMsgHandler): () => void {
    this.anyHandlers.add(fn);
    return () => { this.anyHandlers.delete(fn); };
  }

  /** Called with (true, 0) on open and (false, attempt) on close / each scheduled retry. */
  onStatus(fn: StatusHandler): () => void {
    this.statusHandlers.add(fn);
    return () => { this.statusHandlers.delete(fn); };
  }

  /**
   * Called with the close code when the server ends the connection with one of
   * FINAL_CLOSE_CODES: no reconnect follows, so the room view would otherwise
   * sit frozen on its last snapshot. Returns an unsubscribe function.
   */
  onGiveUp(fn: GiveUpHandler): () => void {
    this.giveUpHandlers.add(fn);
    return () => { this.giveUpHandlers.delete(fn); };
  }

  /** Close deliberately (no reconnect). */
  close(): void {
    this.closedByUser = true;
    this.clearReconnect();
    this.stopPing();
    this.dropSocket();
    if (this._connected) {
      this._connected = false;
      this.emitStatus(false);
    }
  }

  /** Detach and close the current WebSocket without touching reconnect state. */
  private dropSocket(): void {
    const ws = this.ws;
    this.ws = null;
    if (!ws) return;
    ws.onopen = ws.onclose = ws.onmessage = ws.onerror = null;
    try { ws.close(); } catch { /* already closing */ }
  }

  private open(): void {
    if (!this.url) return;
    const ctor = this.wsCtor ?? resolveWebSocket();
    let ws: WebSocketLike;
    try {
      ws = new ctor(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this._connected = true;
      this._attempt = 0;
      this.backoffMs = NET.reconnectBackoffMinMs;
      this.sendHello();
      this.startPing();
      this.emitStatus(true);
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws) return;
      const msg = parseServerMsg(ev.data);
      if (msg) this.dispatch(msg);
    };
    ws.onclose = (ev: unknown) => {
      if (this.ws !== ws) return;
      // 4000 abuse, 4001 out-of-date client, 4003 opened in another tab: retrying would loop forever.
      const code = typeof ev === 'object' && ev !== null && 'code' in ev ? Number((ev as { code: unknown }).code) : 0;
      const final = FINAL_CLOSE_CODES.has(code);
      if (final) this.closedByUser = true;
      this.ws = null;
      this.stopPing();
      const wasConnected = this._connected;
      this._connected = false;
      if (wasConnected) this.emitStatus(false);
      if (final) for (const fn of this.giveUpHandlers) fn(code);
      else if (!this.closedByUser) this.scheduleReconnect();
    };
    ws.onerror = () => { /* the close event that follows drives the retry */ };
  }

  private sendHello(): void {
    const h = this.hello ?? { name: '' };
    const msg: ClientMsg = { t: 'hello', v: PROTOCOL_VERSION, name: h.name };
    if (this._token) msg.token = this._token;
    if (h.color !== undefined) msg.color = h.color;
    this.send(msg);
  }

  private dispatch(msg: ServerMsg): void {
    if (msg.t === 'welcome') this._token = msg.token;
    for (const fn of this.anyHandlers) fn(msg);
    const set = this.handlers.get(msg.t);
    if (set) for (const fn of set) fn(msg);
  }

  private scheduleReconnect(): void {
    if (this.closedByUser || this.reconnectTimer) return;
    this._attempt++;
    const jitter = 1 + this.random() * BACKOFF_JITTER;
    const delay = Math.round(this.backoffMs * jitter);
    this.backoffMs = Math.min(this.backoffMs * 2, NET.reconnectBackoffMaxMs);
    this.emitStatus(false);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.closedByUser) this.open();
    }, delay);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => this.send({ t: 'ping', c: this.now() }), NET.pingIntervalMs);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private emitStatus(connected: boolean): void {
    for (const fn of this.statusHandlers) fn(connected, this._attempt);
  }
}

/**
 * Parse one inbound frame. The server is trusted for field shapes; this only
 * rejects non-JSON, non-objects and unknown `t` so a handler never sees junk.
 */
export function parseServerMsg(data: unknown): ServerMsg | null {
  if (typeof data !== 'string') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
  const t = (parsed as { t?: unknown }).t;
  if (typeof t !== 'string' || !SERVER_TYPES.has(t)) return null;
  return parsed as ServerMsg;
}
